import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import React from 'react';
import {
  ComposedChart,
  CartesianGrid,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Line,
} from 'recharts';
import { useAxisStyles, useChartColors } from 'src/hooks/colors/useChartColors';
import { TooltipContent } from '../../atoms/tooltips/TooltipContent';
import { useLegendContext } from '../Legend/LegendContext';

export type NewAreaGraphProps = {
  data: Array<object>;
  xAxisKey: string;
  benchmarkKey?: string;
  xAxisFormatter?: (value: number | string) => string;
  yAxisFormatter?: (value: number) => string;
  tooltip?: ReactJSXElement;
  stacked?: boolean;
  percentRange?: boolean;
};

export const NewAreaGraph: React.FC<NewAreaGraphProps> = ({
  data,
  xAxisKey,
  benchmarkKey,
  xAxisFormatter = (value: number | string) => value.toString(),
  yAxisFormatter = (value: number) => value.toString(),
  tooltip,
  stacked = false,
  percentRange,
}) => {
  const { selectedBaseLegendValues, selectedLineLegendValues } =
    useLegendContext();

  const { benchmarkLineColor, gridColor } = useChartColors();

  const axisStyles = useAxisStyles();

  return (
    <ResponsiveContainer>
      <ComposedChart data={data}>
        <CartesianGrid
          strokeDasharray="4 4"
          strokeWidth={1}
          stroke={gridColor}
          strokeOpacity={0.4}
          fill="transparent"
          vertical={false}
        />
        <YAxis
          width={70}
          domain={percentRange ? [0, 100] : undefined}
          tickFormatter={yAxisFormatter}
          {...axisStyles}
          axisLine={false}
        />
        <XAxis
          type="number"
          domain={['auto', 'auto']}
          tickFormatter={xAxisFormatter}
          scale={'linear'}
          dataKey={xAxisKey}
          {...axisStyles}
          axisLine={false}
        />
        {tooltip ?? (
          <Tooltip
            offset={25}
            content={
              <TooltipContent
                valueFormatter={yAxisFormatter}
                labelFormatter={xAxisFormatter}
              />
            }
          />
        )}
        {selectedBaseLegendValues.map(({ color, key }, index) => {
          const lineColor = key === benchmarkKey ? benchmarkLineColor : color;
          return (
            <>
              <defs>
                <linearGradient
                  id={`colorPv_${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop stopColor={lineColor} stopOpacity="0.7" />
                  <stop offset="1" stopColor={lineColor} stopOpacity="0.05" />
                </linearGradient>
              </defs>
              <Area
                stackId={stacked ? 'a' : key}
                key={key}
                dataKey={key}
                connectNulls
                isAnimationActive={false}
                stroke={lineColor}
                dot={false}
                fill={`url(#colorPv_${index})`}
                activeDot={{
                  stroke: lineColor,
                  fill: lineColor,
                  strokeOpacity: 0.5,
                  strokeWidth: 4,
                  r: 6,
                }}
                strokeWidth={2}
              />
            </>
          );
        })}
        {selectedLineLegendValues.map(({ key, color }) => {
          return (
            <Line
              isAnimationActive={false}
              key={`line_${key}`}
              dataKey={key}
              type="monotone"
              stroke={color}
              dot={false}
              strokeWidth={2}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
